import { DatePicker } from '@mui/lab';
import { Checkbox, Divider, FormControlLabel, Grid, InputAdornment } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import TypePaymentList from 'front-office/components/shared/payment/type-payment';
import { Document } from 'front-office/view-models/release-id-card/document';
import { memo, useMemo, useRef } from 'react';
import MunicipalityList from 'shell/components/shared/municipality-list';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import ReleaseModeIndentityCard from 'shell/components/shared/release-mode';
import PmiTextField from 'shell/components/shared/text-field';
import TypeIndentityCard from 'shell/components/shared/type-indentity-card';
import { noop } from 'shell/utils/noop';
import ListYesNo from 'shell/components/shared/list-yes-no';
import { DateTime } from 'luxon';
import ValidityList from './select-validity';
import { getValidity } from 'front-office/utils/release-id-card-document-transformer';

interface IdentityCardFormProps {
  onChange: (key: keyof Document, value: Document[keyof Document]) => void;
  onChangeValues: (values: Document) => void;
  values?: Document;
  birthday?: DateTime | null;
  errors?: FormikErrors<Document>;
  touched?: FormikTouched<Document>;
  isResident?: boolean;
}

const IdentityCardFormComponent = ({
  onChange,
  onChangeValues,
  values,
  errors,
  birthday,
  isResident,
  touched,
}: IdentityCardFormProps): React.ReactElement => {
  const referenceExpirationDate = useRef<DateTime | null>(values?.expirationDate || null);
  const getExpirationDateForAsylum: DateTime = useMemo(() => {
    const annoInCorso = DateTime.local().year;
    const currentDay = DateTime.now().get('day');
    const currentMonth = DateTime.now().get('month');

    const giornoDiNascita = birthday?.get('day');
    const meseDiNascita = birthday?.get('month');
    let expiryYear = annoInCorso + 3;
    let expiryMonth = meseDiNascita;
    let expiryDay = giornoDiNascita;

    if (giornoDiNascita && meseDiNascita) {
      if (giornoDiNascita < currentDay && meseDiNascita < currentMonth) {
        expiryYear = expiryYear + 1;
        expiryMonth = meseDiNascita;
        expiryDay = giornoDiNascita;
      }
    }

    return DateTime.now().set({ year: expiryYear, month: expiryMonth, day: expiryDay });
  }, [values?.isRequestedAsylum, values?.expirationDate, birthday]);

  return (
    <Grid container spacing={2}>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Carta precedente"
            aria-label="Carta precedente"
            id="previousCard"
            name="previousCard"
            fullWidth
            onChange={(event) => {
              onChange('previousCard', event?.target.value);
            }}
            inputProps={{
              maxLength: '9',
            }}
            disabled={values?.hasPreviousCard}
            value={values?.previousCard || ''}
            error={touched?.previousCard && Boolean(errors?.previousCard)}
            helperText={errors?.previousCard}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data scadenza"
            aria-label="Data scadenza"
            value={values?.expirationDateOld || null}
            onChange={(date) => {
              onChangeValues({
                expirationDateOld: date,
                stillValid: getValidity(date?.toFormat('yyyy-MM-dd')),
              });
            }}
            disabled={values?.hasPreviousCard}
            renderInput={(params) => <PmiTextField {...params} fullWidth helperText="" />}
          />
        </Grid>
        <Grid item container spacing={2} xs={12}>
          <Grid item xs={12} md={7}>
            {/* <MunicipalityList
              label="Comune"
              aria-label="Comune"
              value={
                values?.previousMunicipality
                  ? {
                      id: values?.previousMunicipality?.codiceIstat || '',
                      description: values?.previousMunicipality?.nomeComune || '',
                      province: values?.previousMunicipality?.siglaProvinciaIstat || '',
                    }
                  : null
              }
              onChange={noop}
              disabled={values?.hasPreviousCard}
            /> */}
            <PmiTextField
              label="Comune"
              id="previousMunicipality"
              aria-label="Comune"
              fullWidth
              value={values?.previousMunicipality?.nomeComune || ''}
              onChange={(event) => {
                onChange('previousMunicipality', {
                  ...values?.previousMunicipality,
                  codiceIstat: '',
                  nomeComune: event?.target.value,
                });
              }}
              disabled={values?.hasPreviousCard}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="provincePreviousMunicipality"
              label="Provincia o Stato Estero"
              aria-label="Provincia o Stato Estero"
              fullWidth
              value={values?.previousMunicipality?.siglaProvinciaIstat || ''}
              onChange={(event) => {
                onChange('previousMunicipality', {
                  ...values?.previousMunicipality,
                  siglaProvinciaIstat: event?.target.value,
                });
              }}
              disabled={values?.hasPreviousCard}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <ValidityList
            label="Validità"
            onChange={(value) => {
              onChange('stillValid', value);
            }}
            disabled={values?.hasPreviousCard}
            value={values?.stillValid || ''}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ListYesNo
            required
            label="Carta resa"
            value={values?.yieldCard || ''}
            onChange={(event) => onChange('yieldCard', event.target.value)}
            error={touched?.yieldCard && Boolean(errors?.yieldCard)}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Numero *"
          aria-label="Numero"
          id="number"
          name="number"
          fullWidth
          onChange={(event) => {
            onChange('number', event?.target.value);
          }}
          inputProps={{
            maxLength: '3',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">{values?.fixedLotNumber}</InputAdornment>
            ),
          }}
          value={values?.number || ''}
          error={touched?.number && Boolean(errors?.number)}
          helperText={errors?.number}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TypeIndentityCard label="Tipo" value={values?.type || ''} onChange={noop} disabled />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="isExpatriationBan"
              checked={values?.isExpatriationBan || false}
              onChange={() =>
                onChangeValues({
                  isExpatriationBan: !values?.isExpatriationBan,
                  parentsPrint: values?.isExpatriationBan ? '' : '0',
                })
              }
              inputProps={{ 'aria-label': 'Interdizione espatrio' }}
              disabled={values?.disableExpatriationBan || false}
            />
          }
          label="Interdizione espatrio"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="isRequestedAsylum"
              checked={values?.isRequestedAsylum || false}
              onChange={() =>
                onChangeValues({
                  isRequestedAsylum: !values?.isRequestedAsylum,
                  expirationDate: values?.isRequestedAsylum
                    ? referenceExpirationDate.current
                    : getExpirationDateForAsylum,
                })
              }
              inputProps={{ 'aria-label': 'Richiedente asilo' }}
            />
          }
          label="Richiedente asilo"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Data rilascio"
          aria-label="Data rilascio"
          value={values?.releaseDate || null} // valore fisso data corrente
          onChange={noop}
          disabled
          renderInput={(params) => <PmiTextField {...params} fullWidth helperText="" />}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Data scadenza"
          aria-label="Data scadenza"
          value={values?.expirationDate || null}
          disabled
          onChange={noop}
          renderInput={(params) => <PmiTextField {...params} fullWidth helperText="" />}
        />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={4}>
          <ReleaseModeIndentityCard
            required
            value={values?.releaseMode || ''}
            onChange={(value) => onChange('releaseMode', value)}
            error={touched?.releaseMode && Boolean(errors?.releaseMode)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TypePaymentList
            required
            value={values?.typePayment || ''}
            onChange={(event) => onChange('typePayment', event.target.value)}
            error={touched?.typePayment && Boolean(errors?.typePayment)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ListYesNo
            required
            label="Stampa Stato Civile"
            value={values?.printCivilStatus || ''}
            onChange={(event) => onChange('printCivilStatus', event.target.value)}
            error={touched?.printCivilStatus && Boolean(errors?.printCivilStatus)}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={!isResident ? 4 : 5}>
        {values?.showParents ? (
          <ListYesNo
            label="Stampa genitori *"
            value={values?.parentsPrint || ''}
            expatriationBan={values?.isExpatriationBan || false}
            onChange={(event) => onChange('parentsPrint', event.target.value)}
            error={touched?.parentsPrint && Boolean(errors?.parentsPrint)}
          />
        ) : null}
      </Grid>
      <Grid item xs={12} md={!isResident ? 4 : 5}>
        <ListYesNo
          required
          label="Servizio a domicilio"
          value={values?.domicile || ''}
          onChange={(event) => onChange('domicile', event.target.value)}
          error={touched?.domicile && Boolean(errors?.domicile)}
        />
      </Grid>
      {!isResident ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="Num.Prot. Nulla Osta"
            aria-label="numero-protocollo-nulla-osta"
            id="numOfClearanceDocument"
            name="numOfClearanceDocument"
            fullWidth
            onChange={(event) => {
              onChange('numOfClearanceDocument', event?.target.value);
            }}
            inputProps={{
              maxLength: '50',
            }}
            value={values?.numOfClearanceDocument || ''}
            error={touched?.numOfClearanceDocument && Boolean(errors?.numOfClearanceDocument)}
            helperText={errors?.numOfClearanceDocument}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Comune rilascio</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} md={7}>
        <MunicipalityList
          label="Comune"
          aria-label="Comune"
          value={values?.municipality || null} // valore fisso ente collegato
          onChange={noop}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Provincia"
          inputProps={{
            maxLength: '2',
          }}
          aria-label="Provincia"
          fullWidth
          value={values?.municipality?.province || ''} // valore fisso ente collegato
          onChange={noop}
          disabled
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Statura *"
          aria-label="Statura"
          id="stature"
          name="stature"
          fullWidth
          onChange={(event) => {
            onChange('stature', event?.target.value);
          }}
          value={values?.stature || ''}
          error={touched?.stature && Boolean(errors?.stature)}
          helperText={errors?.stature}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Capelli *"
          aria-label="Capelli"
          id="hair"
          name="hair"
          fullWidth
          onChange={(event) => {
            onChange('hair', event?.target.value);
          }}
          value={values?.hair || ''}
          error={touched?.hair && Boolean(errors?.hair)}
          helperText={errors?.hair}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Occhi *"
          aria-label="Occhi"
          id="eyes"
          name="eyes"
          fullWidth
          onChange={(event) => {
            onChange('eyes', event?.target.value);
          }}
          value={values?.eyes || ''}
          error={touched?.eyes && Boolean(errors?.eyes)}
          helperText={errors?.eyes}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <PmiTextField
          label="Segni particolari"
          aria-label="Segni particolari"
          id="particularSigns"
          name="particularSigns"
          fullWidth
          onChange={(event) => {
            onChange('particularSigns', event?.target.value);
          }}
          value={values?.particularSigns || ''}
          error={touched?.particularSigns && Boolean(errors?.particularSigns)}
          helperText={errors?.particularSigns}
        />
      </Grid>
    </Grid>
  );
};

IdentityCardFormComponent.displayName =
  'Release id card form - document - identity card form component';

const IdentityCardForm = memo(IdentityCardFormComponent);

export default IdentityCardForm;
